import { CoreApi, Product, ProductPartial, ProductType } from '@inter-ikea-storageone/range';
import { ComponentIds, Entity, WorldTransformComponent } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { getCountry, isNAMarket } from 'i18n/i18nUtils';

const frameIsWallHanged = (frame: Entity, coreApi: CoreApi) => {
  return (
    !coreApi.entities.isEntityPlacedOnFloor(frame) &&
    !coreApi.entities.isPartOfFloorstandingStack(frame)
  );
};

const frameIsAboveAllowedHeight = (frame: Entity) => {
  const framePosition = frame.getComponent<WorldTransformComponent>(
    ComponentIds.WORLD_TRANSFORM_COMPONENT_ID
  )?.transform.position;
  const frameSize = frame.getComponent<CommunicationSizeComponent>(
    ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
  )?.size;
  return (
    framePosition &&
    frameSize &&
    framePosition.Y - frameSize.height / 2 >= getMarketMaxDrawerHeight()
  );
};

/**
 * Check if selected frame can have interior suggestion or front layout with drawers or wire baskets
 * @param frame selected frame
 * @param assemblyProduct interior suggestions or front layout product
 * @param coreApi
 * @returns false if assembly includes drawers or wirebaskets and frame is wall hanged or above 120cm
 */
export const framePlacementOkForAssembly = (
  frame: Entity,
  assemblyProduct: Product,
  coreApi: CoreApi
) => {
  if (
    getCountry() === 'KR' &&
    assemblyProduct.communicatedDimensions.height >= 1200 &&
    assemblyProduct.parts.every(
      part =>
        part.product?.type === ProductType.SMASTAD_DRAWER_INSERT ||
        part.product?.type === ProductType.SMASTAD_DRAWER_FRONT
    )
  ) {
    // 120 or 180 cm high assemblies with only drawer not allowed in South Korea
    return false;
  }
  const assemblyIncludesDrawerOrWirebasket = assemblyProduct.parts.some(
    part =>
      part.product?.type === ProductType.SMASTAD_WIRE_BASKET_ASM ||
      part.product?.type === ProductType.SMASTAD_DRAWER_INSERT
  );
  return assemblyIncludesDrawerOrWirebasket
    ? !frameIsAboveAllowedHeight(frame) && !frameIsWallHanged(frame, coreApi)
    : true;
};

/**
 * Check if selected frame can have a specific interior
 * @param frame selected frame
 * @param assemblyProduct interior suggestions or front layout product
 * @param coreApi
 * @returns false if interior is drawers or wirebaskets and frame is wall hanged or above 120cm
 */
export const framePlacementOkForInterior = (
  frame: Entity,
  interior: Product | ProductPartial,
  coreApi: CoreApi
) => {
  return [ProductType.SMASTAD_WIRE_BASKET_ASM, ProductType.SMASTAD_DRAWER_ASM].includes(
    interior.type
  )
    ? !frameIsAboveAllowedHeight(frame) && !frameIsWallHanged(frame, coreApi)
    : true;
};

export const getMarketMaxDrawerHeight = () => {
  return isNAMarket() ? 1360 : 1200;
};
